import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '@/api/hosts';
import { Role } from '@/api/queries/useGetRoles';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface UpdateRoleData {
  roleId: string;
  name?: string;
  permissions?: string[];
}

export const useUpdateRole = (organisationId?: string) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<Role, Error, UpdateRoleData>({
    mutationFn: async ({ roleId, ...data }) => {
      const response = await fetch(
        `${APIBaseChronos}/api/roles/${roleId}${organisationId ? `?orgId=${encodeURIComponent(organisationId)}` : ''}`,
        getFetchConfig({ method: FetchMethod.PATCH, data }),
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message ?? 'Failed to update role');
      }

      return response.json();
    },
    onSuccess: (updatedRole) => {
      queryClient.setQueryData([QueryEntity.Roles, { organisationId }], (oldRoles: Role[] | undefined) => {
        if (!oldRoles) return [updatedRole];

        return oldRoles.map((role) => (role.id === updatedRole.id ? updatedRole : role));
      });
      toast.success('Role updated successfully');
      queryClient.invalidateQueries([QueryEntity.User]);
    },
    onError: (error) => {
      toast.error(error.message ?? 'Failed to update role');
    },
  });
};
